import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AnalyticsGranularity,
  SearchBreakdown,
  SearchFunnelSeriesResponse,
} from "../../api/analyticsAdmin";
import {
  buildTrend,
  formatBucketTick,
  RateMetric,
  TrendMode,
  VolumeMetric,
} from "../../helpers/searchTrend";
import { formatRate } from "../../helpers/searchAnalytics";
import { formatInteger } from "../../helpers/formatNumber";
import SectionBody from "./SectionBody";
import { SectionState } from "./useAnalyticsStream";

const MODES: { key: TrendMode; label: string }[] = [
  { key: "Volume", label: "Volume" },
  { key: "Conversion", label: "Conversion" },
];

const BREAKDOWNS: { key: SearchBreakdown; label: string }[] = [
  { key: "None", label: "No breakdown" },
  { key: "Gender", label: "Gender" },
  { key: "AgeBand", label: "Age band" },
  { key: "TypeaheadOrigin", label: "Typeahead origin" },
];

const VOLUME_METRICS: { key: VolumeMetric; label: string }[] = [
  { key: "searches", label: "Searches" },
  { key: "opens", label: "Opens" },
  { key: "outbounds", label: "Outbound" },
  { key: "uniqueSearchers", label: "Searchers" },
];

const RATE_METRICS: { key: RateMetric; label: string }[] = [
  { key: "searchToOpenRate", label: "Search → open" },
  { key: "searchToOutboundRate", label: "Search → outbound" },
];

const GRANULARITIES: AnalyticsGranularity[] = ["Day", "Week", "Month"];

// Breakdown and granularity shape the request, so they live on the page with the
// batch; mode and the metric pickers only re-read data already in hand.
const SearchFunnelTrend: React.FC<{
  section: SectionState<SearchFunnelSeriesResponse>;
  breakdown: SearchBreakdown;
  onBreakdownChange: (breakdown: SearchBreakdown) => void;
  granularity: AnalyticsGranularity;
  onGranularityChange: (granularity: AnalyticsGranularity) => void;
}> = ({
  section,
  breakdown,
  onBreakdownChange,
  granularity,
  onGranularityChange,
}) => {
  const [mode, setMode] = useState<TrendMode>("Volume");
  const [volumeMetric, setVolumeMetric] = useState<VolumeMetric>("searches");
  const [rateMetric, setRateMetric] = useState<RateMetric>("searchToOpenRate");

  const chart = useMemo(
    () =>
      buildTrend({
        series: section.status === "ok" ? section.data.series : [],
        mode,
        breakdown,
        volumeMetric,
        rateMetric,
      }),
    [section, mode, breakdown, volumeMetric, rateMetric]
  );

  const showMetricSelect = breakdown !== "None";
  const formatValue = (v: number) =>
    chart.valueKind === "rate" ? formatRate(v) : formatInteger(v);

  return (
    <section className="mb-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-medium">Trend over time</h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {MODES.map((m) => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${
                  mode === m.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <select
            value={breakdown}
            onChange={(e) => onBreakdownChange(e.target.value as SearchBreakdown)}
            className="p-2 border border-gray-300 rounded-lg text-sm"
          >
            {BREAKDOWNS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            ))}
          </select>

          {showMetricSelect && mode === "Volume" && (
            <select
              value={volumeMetric}
              onChange={(e) => setVolumeMetric(e.target.value as VolumeMetric)}
              className="p-2 border border-gray-300 rounded-lg text-sm"
            >
              {VOLUME_METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          )}

          {showMetricSelect && mode === "Conversion" && (
            <select
              value={rateMetric}
              onChange={(e) => setRateMetric(e.target.value as RateMetric)}
              className="p-2 border border-gray-300 rounded-lg text-sm"
            >
              {RATE_METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          )}

          <div className="flex gap-1">
            {GRANULARITIES.map((g) => (
              <button
                key={g}
                onClick={() => onGranularityChange(g)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${
                  granularity === g
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      <SectionBody
        section={section}
        isEmpty={() => chart.rows.length === 0}
        emptyMessage="No search activity for this range."
      >
        {() => (
          <div className="bg-white rounded-lg shadow p-6">
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chart.stacked ? (
                <AreaChart data={chart.rows} margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="bucketStart"
                    tickFormatter={(v: string) => formatBucketTick(v, granularity)}
                  />
                  <YAxis tickFormatter={formatValue} />
                  <Tooltip formatter={(v: number) => formatValue(v)} />
                  <Legend />
                  {chart.series.map((s) => (
                    <Area
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label}
                      stackId="1"
                      stroke={s.color}
                      fill={s.color}
                      fillOpacity={0.6}
                      isAnimationActive={false}
                    />
                  ))}
                </AreaChart>
              ) : (
                <LineChart data={chart.rows} margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="bucketStart"
                    tickFormatter={(v: string) => formatBucketTick(v, granularity)}
                  />
                  <YAxis
                    domain={chart.valueKind === "rate" ? [0, 1] : undefined}
                    tickFormatter={formatValue}
                  />
                  <Tooltip formatter={(v: number) => formatValue(v)} />
                  <Legend />
                  {chart.series.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label}
                      stroke={s.color}
                      // Segment lines (per-segment Conversion) are sparse: break at
                      // gaps and show dots so isolated points stay visible. The
                      // un-split None series are contiguous, so bridge gaps cleanly.
                      dot={breakdown === "None" ? false : { r: 2 }}
                      connectNulls={breakdown === "None"}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
            </div>
          </div>
        )}
      </SectionBody>
    </section>
  );
};

export default SearchFunnelTrend;
